import React, { useCallback, useEffect, useState } from 'react';
import AppModal from '../../../components/AppModal/AppModal';
import { api } from '../../../app/http-common';

/**
 * Edit an existing newsletter's name and style prompt — PRD §10.
 *
 * Deliberately does not offer delete or a "regenerate names" button (answers
 * D4/D7). Saving affects subsequent newsletter jobs only; a job already running
 * uses the configuration snapshot frozen when it was queued (§5.3).
 */
export default function NewsletterSettingsModal({ bucketId, bucketName, onClose }) {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [name, setName] = useState('');
  const [prompt, setPrompt] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getBucketNewsletterConfig(bucketId);
        if (cancelled) return;
        setConfig(res);
        setName(res.name || '');
        setPrompt(res.reformattingPrompt || '');
      } catch (err) {
        if (cancelled) return;
        // 404 simply means this bucket has never had a newsletter created.
        if (err?.response?.status !== 404) {
          setError('Could not load the newsletter settings.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [bucketId]);

  const save = useCallback(async () => {
    setSaving(true);
    setError('');
    try {
      const saved = await api.saveBucketNewsletterConfig(bucketId, {
        name,
        description: config?.description || '',
        reformattingPrompt: prompt,
      }, { silent: true });
      setConfig(saved);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || 'The newsletter settings could not be saved.');
      setSaving(false);
    }
  }, [bucketId, name, prompt, config, onClose]);

  if (loading) {
    return (
      <AppModal isOpen title="Newsletter settings" onCancel={onClose} showCancel={false} footer={<div />} maxWidth={460}>
        <p style={{ margin: 0, fontSize: 14, color: '#3C4A5E' }}>Loading…</p>
      </AppModal>
    );
  }

  if (!config) {
    return (
      <AppModal
        isOpen
        title="Newsletter settings"
        onCancel={onClose}
        showCancel={false}
        confirmLabel="Close"
        onConfirm={onClose}
        maxWidth={460}
      >
        <p style={{ margin: 0, fontSize: 14, color: '#3C4A5E', lineHeight: 1.5 }}>
          <strong>{bucketName}</strong> does not have a newsletter yet. Select
          some stories and choose <strong>Create newsletter</strong> — you will
          be asked to name it and set its style then.
        </p>
      </AppModal>
    );
  }

  return (
    <AppModal
      isOpen
      title="Newsletter settings"
      onCancel={onClose}
      onConfirm={save}
      confirmLabel="Save"
      confirmDisabled={!name.trim() || !prompt.trim() || saving}
      isSubmitting={saving}
      maxWidth={620}
      modalTestId="newsletter-settings-modal"
    >
      <div className="pbnew-nl-section">
        <label className="pbnew-nl-label" htmlFor="pbnew-nl-settings-name">Name</label>
        <input
          id="pbnew-nl-settings-name"
          className="pbnew-nl-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>

      <div className="pbnew-nl-section">
        <label className="pbnew-nl-label" htmlFor="pbnew-nl-settings-prompt">Style and tone</label>
        <textarea
          id="pbnew-nl-settings-prompt"
          className="pbnew-nl-textarea"
          rows={10}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
        />
        <p style={{ margin: '8px 0 0', fontSize: 12, color: '#3C4A5E', lineHeight: 1.5 }}>
          Version {config.version}. Changes apply to newsletters created from
          now on; one already being generated keeps the settings it started with.
        </p>
      </div>
      {error ? <div className="pbnew-nl-error" role="alert">{error}</div> : null}
    </AppModal>
  );
}
